import { Clock } from '../domain/Clock';
import { formatTimestamp, formatClockType } from '../domain/Utils';

/**
 * Interfaz para el estado del filtro
 */
export interface FilterState {
  startDate: string;
  endDate: string;
  active: boolean;
}

/**
 * Interfaz para el estado de paginación
 */
export interface PaginationState {
  currentPage: number;
  itemsPerPage: number;
}

/**
 * Adaptador Vue para manejar la lógica de historial de fichajes
 * Encapsula filtrado, paginación y exportación de datos
 */
export class VueWorkingHoursHistory {
  private clocks: Clock[];
  private filter: FilterState;
  private pagination: PaginationState;

  constructor(clocks: Clock[], filter: FilterState, pagination: PaginationState) {
    this.clocks = clocks;
    this.filter = filter;
    this.pagination = pagination;
  }

  // ============================================
  // Métodos de filtrado
  // ============================================

  /**
   * Filtra los clocks por rango de fechas
   */
  getFilteredClocks(): Clock[] {
    if (!this.filter.active || !this.filter.startDate || !this.filter.endDate) {
      return this.clocks;
    }

    const startDate = new Date(this.filter.startDate);
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date(this.filter.endDate);
    endDate.setHours(23, 59, 59, 999);

    return this.clocks.filter(clock => {
      const clockDate = new Date(clock.timestamp);
      return clockDate >= startDate && clockDate <= endDate;
    });
  }

  /**
   * Valida y aplica el filtro de fechas
   */
  applyFilter(): { success: boolean; error?: string } {
    if (!this.filter.startDate || !this.filter.endDate) {
      return { success: false, error: 'Debe seleccionar ambas fechas' };
    }

    // Validar que la fecha de inicio no sea posterior a la fecha de fin
    if (new Date(this.filter.startDate) > new Date(this.filter.endDate)) {
      return { success: false, error: 'La fecha de inicio no puede ser posterior a la fecha de fin' };
    }

    this.filter.active = true;
    this.pagination.currentPage = 1; // Resetear a la primera página
    return { success: true };
  }

  /**
   * Limpia el filtro
   */
  clearFilter(): void {
    this.filter.startDate = '';
    this.filter.endDate = '';
    this.filter.active = false;
    this.pagination.currentPage = 1;
  }

  // ============================================
  // Métodos de paginación
  // ============================================

  /**
   * Calcula el total de páginas
   */
  getTotalPages(): number {
    const filteredClocks = this.getFilteredClocks();
    return Math.ceil(filteredClocks.length / this.pagination.itemsPerPage);
  }

  /**
   * Calcula el índice de inicio para la paginación
   */
  getStartIndex(): number {
    return (this.pagination.currentPage - 1) * this.pagination.itemsPerPage;
  }

  /**
   * Calcula el índice de fin para la paginación
   */
  getEndIndex(): number {
    const filteredClocks = this.getFilteredClocks();
    const end = this.getStartIndex() + this.pagination.itemsPerPage;
    return Math.min(end, filteredClocks.length);
  }

  /**
   * Obtiene los clocks para la página actual
   */
  getPaginatedClocks(): Clock[] {
    const filteredClocks = this.getFilteredClocks();
    return filteredClocks.slice(this.getStartIndex(), this.getEndIndex());
  }

  /**
   * Avanza a la siguiente página
   */
  nextPage(): boolean {
    if (this.pagination.currentPage < this.getTotalPages()) {
      this.pagination.currentPage++;
      return true;
    }
    return false;
  }

  /**
   * Retrocede a la página anterior
   */
  previousPage(): boolean {
    if (this.pagination.currentPage > 1) {
      this.pagination.currentPage--;
      return true;
    }
    return false;
  }

  /**
   * Cambia el número de elementos por página
   */
  changeItemsPerPage(newItemsPerPage: number): void {
    this.pagination.itemsPerPage = newItemsPerPage;
    this.pagination.currentPage = 1; // Resetear a la primera página
  }

  // ============================================
  // Métodos de exportación
  // ============================================

  /**
   * Genera el contenido CSV de los clocks filtrados
   */
  generateCSVContent(): string {
    const filteredClocks = this.getFilteredClocks();

    if (filteredClocks.length === 0) {
      return '';
    }

    // Headers
    const headers = ['Fecha', 'Tipo', 'Motivo'];

    // Rows
    const rows = filteredClocks.map(clock => {
      const latestChange = clock.changes && clock.changes.length > 0
        ? clock.changes[clock.changes.length - 1]
        : null;

      return [
        formatTimestamp(clock.timestamp),
        formatClockType(clock.type),
        (latestChange && latestChange.justification) ? latestChange.justification : '-'
      ];
    });

    // Combinar headers y rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    return '\ufeff' + csvContent; // BOM para UTF-8
  }

  /**
   * Genera el nombre de archivo para el CSV
   */
  generateCSVFilename(): string {
    if (this.filter.active && this.filter.startDate && this.filter.endDate) {
      return `fichajes_${this.filter.startDate}_a_${this.filter.endDate}.csv`;
    }
    return `fichajes_todos.csv`;
  }

  /**
   * Descarga el CSV con los clocks filtrados
   */
  downloadCSV(): boolean {
    const csvContent = this.generateCSVContent();

    if (!csvContent) {
      return false;
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', this.generateCSVFilename());
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    return true;
  }

  // ============================================
  // Métodos de actualización de datos
  // ============================================

  /**
   * Actualiza la referencia a los clocks
   */
  updateClocks(newClocks: Clock[]): void {
    this.clocks = newClocks;
  }
}
